import { useState } from 'react';
import { useTheme } from '../../styles/ThemeContext';
import { fmtEur } from '../../utils/helpers';

const cellStyle = { padding: '7px 10px' };
const monoSm = { fontFamily: 'monospace', fontSize: 10 };

/**
 * Step 3: Products Selection per Unit
 * Supports inline editing of added products
 */
export function Step3Products({
  units, products, onOpenProductModal, onRemoveProduct, onUpdateProduct, totalQty, totalValue, onBack, onNext
}) {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const { theme } = useTheme();
  const c = theme.colors;

  const editInp = { ...theme.inp, padding: '3px 6px', fontSize: 11, width: '100%', minWidth: 0 };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({ category: p.category || '', product: p.product || '', eqNumber: p.eqNumber || '', qty: p.qty || 1, coo: p.coo || '', hsCode: p.hsCode || '', itemPrice: p.itemPrice || 0, dg: p.dg || 'No', weight: p.weight || '' });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const saveEdit = () => {
    if (!editForm.product.trim()) return;
    onUpdateProduct(editingId, { category: editForm.category.trim(), product: editForm.product.trim(), eqNumber: editForm.eqNumber.trim(), qty: Math.max(1, Number(editForm.qty) || 1), coo: editForm.coo.trim().toUpperCase(), hsCode: editForm.hsCode.trim(), itemPrice: Number(editForm.itemPrice) || 0, dg: editForm.dg, weight: editForm.weight });
    setEditingId(null); setEditForm({});
  };
  const updateField = (field, value) => { setEditForm((prev) => ({ ...prev, [field]: value })); };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span style={theme.sectTag}>STEP 03</span>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Products</h2>
        <div style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 11, color: c.textSecondary }}>
          {totalQty} items · €{fmtEur(totalValue)}
        </div>
      </div>

      {units.map((unit) => {
        const unitProducts = products.filter((p) => p.unitId === unit.id);
        const hasDG = unitProducts.some((p) => p.dg === 'Yes');
        return (
          <div key={unit.id} style={{ marginBottom: 18 }}>
            <div style={{ background: c.bgInput, border: '1px solid ' + c.border, borderRadius: '3px 3px 0 0', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{unit.type} — Unit {unit.number}</span>
              <span style={{ background: c.accentBg, color: c.accent, border: '1px solid ' + c.accentBorder, fontSize: 10, padding: '2px 7px', fontFamily: 'monospace', borderRadius: 2 }}>{unitProducts.length} products</span>
              <span style={{ fontSize: 10, background: unit.export_type === 'Permanent' ? c.purpleBg : c.infoBg, color: unit.export_type === 'Permanent' ? c.purple : c.info, border: '1px solid ' + (unit.export_type === 'Permanent' ? c.purpleBg : c.infoBorder), padding: '2px 7px', borderRadius: 2, fontFamily: 'monospace' }}>{unit.export_type || 'Temporary'}</span>
              {hasDG && (
                <span style={{ fontSize: 10, background: c.errorBg, color: c.error, border: '1px solid ' + c.errorBorder, padding: '2px 7px', borderRadius: 2, fontFamily: 'monospace', fontWeight: 600 }}>⚠️ DG</span>
              )}
              <div style={{ marginLeft: 'auto' }}>
                <button onClick={() => onOpenProductModal(unit.id)} style={theme.btnSm}>+ Add Product</button>
              </div>
            </div>
            {unitProducts.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid ' + c.border, borderTop: 'none' }}>
                <thead>
                  <tr style={{ background: c.bgCard }}>
                    {['#', 'Category', 'Product', 'EQ #', 'Qty', 'CoO', 'HS Code', 'Value', 'DG', ''].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 9, fontFamily: 'monospace', color: c.textSecondary, letterSpacing: 1, borderBottom: '1px solid ' + c.border }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {unitProducts.map((p, i) =>
                    editingId === p.id ? (
                      <tr key={p.id} style={{ borderBottom: '1px solid ' + c.border, background: c.accentBg }}>
                        <td style={{ ...cellStyle, ...monoSm, color: c.textSecondary }}>{String(i + 1).padStart(2, '0')}</td>
                        <td style={cellStyle}><input value={editForm.category} onChange={(e) => updateField('category', e.target.value)} style={editInp} placeholder="Category" /></td>
                        <td style={cellStyle}><input value={editForm.product} onChange={(e) => updateField('product', e.target.value)} style={{ ...editInp, borderColor: !editForm.product.trim() ? c.error : undefined }} placeholder="Product *" /></td>
                        <td style={cellStyle}><input value={editForm.eqNumber} onChange={(e) => updateField('eqNumber', e.target.value)} style={editInp} placeholder="EQ #" /></td>
                        <td style={cellStyle}><input type="number" min="1" value={editForm.qty} onChange={(e) => updateField('qty', e.target.value)} style={{ ...editInp, width: 50, textAlign: 'center' }} /></td>
                        <td style={cellStyle}><input value={editForm.coo} onChange={(e) => updateField('coo', e.target.value.slice(0, 2))} maxLength={2} style={{ ...editInp, width: 40, textTransform: 'uppercase' }} placeholder="NL" /></td>
                        <td style={cellStyle}><input value={editForm.hsCode} onChange={(e) => updateField('hsCode', e.target.value)} style={{ ...editInp, width: 80 }} placeholder="HS Code" /></td>
                        <td style={cellStyle}><input type="number" min="0" step="0.01" value={editForm.itemPrice} onChange={(e) => updateField('itemPrice', e.target.value)} style={{ ...editInp, width: 70 }} placeholder="0.00" /></td>
                        <td style={cellStyle}><select value={editForm.dg} onChange={(e) => updateField('dg', e.target.value)} style={{ ...theme.sel, padding: '3px 4px', fontSize: 10, width: 50 }}><option value="No">No</option><option value="Yes">Yes</option></select></td>
                        <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                          <button onClick={saveEdit} style={{ ...theme.btnP, padding: '2px 7px', fontSize: 10, marginRight: 4 }} title="Save">✓</button>
                          <button onClick={cancelEdit} style={{ ...theme.btnG, padding: '2px 7px', fontSize: 10 }} title="Cancel">✕</button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={p.id} style={{ borderBottom: '1px solid ' + c.border }}>
                        <td style={{ ...cellStyle, ...monoSm, color: c.textSecondary }}>{String(i + 1).padStart(2, '0')}</td>
                        <td style={{ ...cellStyle, fontSize: 11, color: c.textSecondary }}>{p.category}</td>
                        <td style={{ ...cellStyle, fontWeight: 500 }}>{p.product}</td>
                        <td style={{ ...cellStyle, ...monoSm }}>{p.eqNumber || '—'}</td>
                        <td style={{ ...cellStyle, fontFamily: 'monospace', textAlign: 'center' }}>{p.qty}</td>
                        <td style={{ ...cellStyle, ...monoSm }}>{p.coo || '—'}</td>
                        <td style={{ ...cellStyle, ...monoSm }}>{p.hsCode || '—'}</td>
                        <td style={{ ...cellStyle, ...monoSm }}>€{fmtEur(p.itemPrice * p.qty)}</td>
                        <td style={cellStyle}>
                          {p.dg === 'Yes' && (
                            <span style={{ fontSize: 9, background: c.errorBg, color: c.error, border: '1px solid ' + c.errorBorder, padding: '2px 5px', borderRadius: 2, fontFamily: 'monospace' }}>DG</span>
                          )}
                        </td>
                        <td style={cellStyle}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => startEdit(p)} style={{ ...theme.btnG, padding: '4px 10px', fontSize: 11 }}>✏️ Edit</button>
                            <button onClick={() => onRemoveProduct(p.id)} style={{ ...theme.btnDanger, padding: '4px 10px', fontSize: 11 }}>× Delete</button>
                          </div>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            ) : (
              <div style={{ background: c.bgCard, border: '1px solid ' + c.border, borderTop: 'none', padding: 20, textAlign: 'center', color: c.textSecondary, fontSize: 12 }}>
                No products yet — click "+ Add Product"
              </div>
            )}
          </div>
        );
      })}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 16, borderTop: '1px solid ' + c.border }}>
        <button onClick={onBack} style={theme.btnG}>← Back</button>
        <button onClick={onNext} style={theme.btnP}>Preview Packing List →</button>
      </div>
    </div>
  );
}
